/**
 * CSRF Token Management Module
 *
 * Handles automatic CSRF token refresh for Laravel Sanctum-based authentication.
 * Features:
 * - Automatic token refresh on 419 errors
 * - Proactive refresh based on user activity
 * - Periodic refresh for active sessions
 * - Visual notifications during token refresh
 * - Request queuing during token refresh operations
 */

import axios from 'axios';
import * as Sentry from '@sentry/react';

// Constants for token refresh configuration
const TOKEN_REFRESH_CONFIG = {
    TOKEN_LIFETIME: 2 * 60 * 60 * 1000, // 2 hours in milliseconds
    REFRESH_THRESHOLD: 30 * 60 * 1000, // 30 minutes
    PERIODIC_REFRESH_INTERVAL: 55 * 60 * 1000, // 55 minutes
    USER_ACTIVITY_THRESHOLD: 5 * 60 * 1000, // 5 minutes
    NOTIFICATION_SUCCESS_DURATION: 2000 // 2 seconds
};

// Track refresh state and queue failed requests
let isRefreshing = false;
let failedQueue = [];

/**
 * Process queued requests after token refresh completes
 * @param {Error|null} error - Error if refresh failed
 * @param {string|null} token - New CSRF token if refresh succeeded
 */
const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });

    failedQueue = [];
};

/**
 * Refresh CSRF token by calling Sanctum endpoints
 * @returns {Promise<string>} New CSRF token
 * @throws {Error} If token refresh fails
 */
async function refreshCsrfToken() {
    // Refreshing CSRF token in progress

    try {
        // Refresh the CSRF cookie via Sanctum
        const cookieResponse = await fetch('/sanctum/csrf-cookie', {
            credentials: 'include',
            headers: {
                'Accept': 'application/json',
            }
        });

        if (!cookieResponse.ok) {
            throw new Error('Failed to refresh CSRF cookie');
        }

        // Get fresh token from API endpoint
        const tokenResponse = await fetch('/api/csrf-token', {
            credentials: 'include',
            headers: {
                'Accept': 'application/json',
            }
        });

        if (tokenResponse.ok) {
            const data = await tokenResponse.json();
            if (data.token) {
                updateCsrfMetaTag(data.token);
                // CSRF token refreshed successfully
                return data.token;
            }
        }

        throw new Error('Could not retrieve CSRF token after refresh');
    } catch (error) {
        console.error('Failed to refresh CSRF token:', error);
        throw error;
    }
}

/**
 * Update the CSRF meta tag in the document head
 * @param {string} token - New CSRF token value
 */
function updateCsrfMetaTag(token) {
    const metaTag = document.querySelector('meta[name="csrf-token"]');
    if (metaTag) {
        metaTag.setAttribute('content', token);
    }
}


/**
 * Visual notification system for token refresh operations
 * Shows user-friendly notifications during CSRF token refresh
 */
const TokenRefreshNotification = {
    notificationId: 'csrf-reconnecting-notification',

    show() {
        if (document.getElementById(this.notificationId)) {
            return;
        }

        const notification = document.createElement('div');
        notification.id = this.notificationId;
        notification.className = 'fixed top-4 right-4 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg z-[9999] flex items-center space-x-2';
        notification.innerHTML = `
            <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Refreshing session, please wait...</span>
        `;
        document.body.appendChild(notification);
    },

    hideWithSuccess() {
        const notification = document.getElementById(this.notificationId);
        if (notification) {
            notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-[9999] flex items-center space-x-2';
            notification.innerHTML = `
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span>Session refreshed</span>
            `;

            setTimeout(() => {
                notification.remove();
            }, TOKEN_REFRESH_CONFIG.NOTIFICATION_SUCCESS_DURATION);
        }
    },

    hide() {
        document.getElementById(this.notificationId)?.remove();
    }
};

/**
 * BRO-1006: single, app-level prompt shown when a 419 cannot be silently
 * recovered (refresh failed, or the one retry still came back 419). It replaces
 * the old red "Session refresh failed" toast AND the surprise auto-reload, and
 * it is the reason no component should ever render the raw "CSRF token mismatch"
 * message: the interceptor marks the rejected error `sessionExpired` and owns
 * the UX here. Persistent (no auto-dismiss) with an explicit Reload button so
 * the user controls when their in-progress input is discarded.
 */
const SessionExpiredPrompt = {
    promptId: 'session-expired-prompt',

    show() {
        // Idempotent — never stack, and clear the in-progress spinner first.
        TokenRefreshNotification.hide();
        if (document.getElementById(this.promptId)) {
            return;
        }

        const prompt = document.createElement('div');
        prompt.id = this.promptId;
        prompt.className = 'fixed top-4 right-4 max-w-sm bg-white border border-gray-200 text-gray-900 px-4 py-3 rounded-lg shadow-xl z-[9999] flex items-start gap-3';
        prompt.setAttribute('role', 'alert');
        prompt.innerHTML = `
            <svg class="h-5 w-5 text-amber-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <div class="text-sm">
                <p class="font-semibold">Session timed out</p>
                <p class="mt-0.5 text-gray-600">Your session timed out for security. Reload the page to continue.</p>
                <button type="button" class="mt-2 inline-flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1">
                    Reload page
                </button>
            </div>
        `;
        prompt.querySelector('button')?.addEventListener('click', () => {
            window.location.reload();
        });
        document.body.appendChild(prompt);
    }
};

/**
 * Setup axios request/response interceptors for CSRF handling
 * - Adds CSRF token to all requests
 * - Handles 419 (token mismatch) errors with automatic refresh
 * - Queues requests during token refresh
 */
export function setupAxiosInterceptors() {
    // Request interceptor to add CSRF token
    axios.interceptors.request.use(
        config => {
            // Always get fresh CSRF token from meta tag
            const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
            if (token) {
                config.headers['X-CSRF-TOKEN'] = token;
            }

            // Ensure we send cookies
            config.withCredentials = true;

            return config;
        },
        error => {
            return Promise.reject(error);
        }
    );

    // Response interceptor to handle 419 (CSRF token mismatch)
    axios.interceptors.response.use(
        response => response,
        async error => {
            const originalRequest = error.config;

            // BRO-1233: the team-seat gate ejected us mid-session. Hard-navigate
            // to the explanation page instead of letting every polling component
            // render its own error. Keyed on error_type rather than the bare 403
            // so ordinary permission failures still surface normally.
            //
            // NOTE: this file is still .js against the project's .ts standard.
            // Converting 398 lines on the request path inside an entitlement
            // ticket mixes unrelated regression risk; see BRO-1233 plan decision
            // P5. BRO-1234 tracks the migration.
            if (error.response?.status === 403
                && error.response?.data?.error_type === 'team_access_ended') {
                window.location.href = '/team-access-ended';
                // Never settles: navigation is already in flight, and rejecting
                // here would let callers render an error over the departing page.
                return new Promise(() => {});
            }

            // BRO-1006: the one retry already happened and we STILL got a 419 —
            // the session is unrecoverable (expired, not just a rotated token).
            // Show the friendly prompt, mark the error handled so no component
            // renders the raw "CSRF token mismatch", and reject.
            if (error.response?.status === 419 && originalRequest._retry) {
                SessionExpiredPrompt.show();
                error.sessionExpired = true;
                return Promise.reject(error);
            }

            // Check if error is 419 (CSRF token mismatch)
            if (error.response?.status === 419 && !originalRequest._retry) {
                // CSRF token expired, attempting to refresh

                if (isRefreshing) {
                    // Token refresh is already in progress, queue this request
                    return new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    }).then(token => {
                        originalRequest.headers['X-CSRF-TOKEN'] = token;
                        return axios(originalRequest);
                    }).catch(err => {
                        return Promise.reject(err);
                    });
                }

                originalRequest._retry = true;
                isRefreshing = true;

                // Show reconnecting notification
                TokenRefreshNotification.show();

                try {
                    const newToken = await refreshCsrfToken();

                    // Update the token in the original request
                    originalRequest.headers['X-CSRF-TOKEN'] = newToken;

                    processQueue(null, newToken);

                    // Hide reconnecting notification
                    TokenRefreshNotification.hideWithSuccess();

                    // Retry the original request
                    return axios(originalRequest);
                } catch (refreshError) {
                    processQueue(refreshError, null);

                    // BRO-1006: refresh failed — surface the single friendly
                    // prompt and mark the error handled so components don't also
                    // render the raw server message. No auto-reload: the prompt's
                    // Reload button lets the user choose when to lose their input.
                    SessionExpiredPrompt.show();
                    refreshError.sessionExpired = true;

                    return Promise.reject(refreshError);
                } finally {
                    isRefreshing = false;
                }
            }

            // Handle 401 (Unauthenticated) - Session expired (BRO-458)
            if (error.response?.status === 401) {
                // Store current URL for redirect after login
                sessionStorage.setItem('intended_url', window.location.pathname + window.location.search);
                // Redirect to login page
                window.location.href = '/login?session_expired=1';
                return Promise.reject(error);
            }

            // BRO-877: Auto-capture network failures and 5xx server errors at
            // the interceptor level — covers ~80% of consequential errors with
            // zero per-call-site changes. 4xx (validation) and 419/401
            // (handled above) are NOT captured: they are routine and would
            // create alert fatigue. Cancellations (BRO-766: deliberate aborts
            // when the user switches contacts) are also NOT captured.
            try {
                // Skip deliberate cancellations — these are app behaviour, not errors.
                if (axios.isCancel(error) || error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
                    // intentionally swallowed — no captureException
                } else if (!error.response) {
                    Sentry.captureException(error, {
                        tags: { error_class: 'network_failure' },
                        extra: {
                            url: originalRequest?.url,
                            method: originalRequest?.method,
                        },
                    });
                } else if (error.response.status >= 500 && error.response.status < 600) {
                    Sentry.captureException(error, {
                        tags: {
                            error_class: 'http_5xx',
                            http_status: String(error.response.status),
                        },
                        extra: {
                            url: originalRequest?.url,
                            method: originalRequest?.method,
                        },
                    });
                }
            } catch (sentryErr) {
                // Belt-and-braces: never let Sentry's own machinery break the
                // user's request flow. Drop the error report rather than throw.
                console.warn('Sentry captureException failed:', sentryErr);
            }

            return Promise.reject(error);
        }
    );
}

/**
 * Setup proactive CSRF token refresh mechanisms
 * - Refreshes token when page becomes visible after being hidden
 * - Periodically refreshes token for active users
 * - Tracks user activity to determine refresh needs
 */
export function setupProactiveTokenRefresh() {
    let lastActivity = Date.now();

    // Track user activity
    const updateActivity = () => {
        lastActivity = Date.now();
    };

    // Listen for user activity
    ['mousedown', 'keydown', 'scroll', 'touchstart'].forEach(event => {
        document.addEventListener(event, updateActivity, { passive: true });
    });

    // Check and refresh token when page becomes visible
    document.addEventListener('visibilitychange', async () => {
        if (!document.hidden) {
            const timeSinceActivity = Date.now() - lastActivity;

            // Page became visible, checking CSRF token

            // Refresh token if page was hidden for more than threshold
            if (timeSinceActivity > TOKEN_REFRESH_CONFIG.REFRESH_THRESHOLD) {
                // Proactively refreshing CSRF token after inactivity
                try {
                    await refreshCsrfToken();
                    // CSRF token proactively refreshed
                } catch (error) {
                    console.error('Failed to proactively refresh CSRF token:', error);
                }
            }
        }
    });

    // Periodically refresh token for active users
    setInterval(async () => {
        const timeSinceActivity = Date.now() - lastActivity;

        // If user has been active recently, refresh token
        if (timeSinceActivity < TOKEN_REFRESH_CONFIG.USER_ACTIVITY_THRESHOLD) {
            // Periodic CSRF token refresh for active user
            try {
                await refreshCsrfToken();
                // CSRF token periodically refreshed
            } catch (error) {
                console.error('Failed to periodically refresh CSRF token:', error);
            }
        }
    }, TOKEN_REFRESH_CONFIG.PERIODIC_REFRESH_INTERVAL);
}

/**
 * Initialize complete CSRF token handling system
 * Call this once during app initialization
 */
export function initializeCsrfHandling() {
    setupAxiosInterceptors();
    setupProactiveTokenRefresh();
    // CSRF token handling initialized
}